/**
 * Studio bridge — inert by default, activated by `?studio=1` on the URL.
 *
 * When the registration app is loaded inside theme-studio's iframe with
 * the flag set, this module:
 *
 *   - Installs a `message` listener that accepts two payload types from
 *     the parent (same-origin check enforced):
 *       { type: 'theme-studio:tokens', tokens: { '--tn-...': value } }
 *       { type: 'theme-studio:fonts-css', css: '@font-face {...}' }
 *   - Posts `{ type: 'theme-studio:ready' }` to the parent on load so
 *     the studio knows to send the current state.
 *
 * Outside studio mode (every production code path), the module exits
 * before installing anything — the bridge is a no-op in the popup and
 * redirect deployments. We don't gate on `import.meta.env.DEV` because
 * studio iframes the production build; the `?studio=1` URL flag is the
 * explicit opt-in.
 *
 * Token application is via `documentElement.style.setProperty()` for
 * tokens prefixed with `--tn-` only. Same defense-in-depth boundary the
 * studio enforces on its own end.
 */

function isAllowedOrigin(origin: string): boolean {
  if (origin === window.location.origin) return true;
  // Studio default port — covers `npm run dev` from theme-studio.
  return /^https?:\/\/(localhost|127\.0\.0\.1):5173$/.test(origin);
}

let fontStyleEl: HTMLStyleElement | null = null;
function ensureFontStyleEl(): HTMLStyleElement {
  if (fontStyleEl) return fontStyleEl;
  fontStyleEl = document.createElement('style');
  fontStyleEl.id = 'tn-studio-fonts';
  document.head.appendChild(fontStyleEl);
  return fontStyleEl;
}

function applyTokens(tokens: Record<string, string | null | undefined>): void {
  const root = document.documentElement;
  for (const [cssVar, value] of Object.entries(tokens)) {
    if (typeof cssVar !== 'string' || !cssVar.startsWith('--tn-')) continue;
    if (value == null || value === '') {
      root.style.removeProperty(cssVar);
    } else {
      root.style.setProperty(cssVar, String(value));
    }
  }
}

function postReady(targetOrigin: string): void {
  try {
    window.parent?.postMessage({ type: 'theme-studio:ready' }, targetOrigin);
  } catch {
    /* parent inaccessible (not in iframe) */
  }
}

export function maybeInstallStudioBridge(): void {
  const params = new URLSearchParams(window.location.search);
  if (params.get('studio') !== '1') return;

  window.addEventListener('message', (event: MessageEvent) => {
    if (!isAllowedOrigin(event.origin)) return;
    const data = event.data;
    if (!data || typeof data !== 'object') return;

    switch (data.type) {
      case 'theme-studio:hello':
        // Parent is probing for liveness — confirm we're listening.
        // Reply directly to the asker's origin (no '*' wildcard since
        // we now know exactly who's asking).
        postReady(event.origin);
        return;
      case 'theme-studio:tokens':
        if (data.tokens && typeof data.tokens === 'object') applyTokens(data.tokens);
        return;
      case 'theme-studio:fonts-css':
        ensureFontStyleEl().textContent = String(data.css ?? '');
        return;
    }
  });

  // Proactive ready in case the parent already has its listener up.
  // Targets '*' because we don't know parent's exact origin yet — the
  // payload is non-sensitive ("I'm ready"), so a wildcard is fine here.
  postReady('*');
}
